import { useCallback, useEffect, useRef, useState } from "react"
import { ClassroomObjectType } from "./AdminStudentsClassroom"
import TabsContentHeader from "../../components/AdminDashboardTabsContentHeader/TabsContentHeader"
import { formatClassroomStudentsCount, getScreenDimensions } from "../../utils/helper"
import images from "../../assets/images"
import { debounce } from "lodash"
import './AdminStudents.scss';
import { Checkbox, CircularProgress, Tooltip } from "@mui/material"
import { useLazyQuery } from "@apollo/client"
import { GET_STUDENTS_BY_CLASSROOM } from "./api/AdminStudentsQuery"
import Loader from "../../components/Loader/Loader"
import ToasterWithMessage from "../../components/toaster"
import SwitchClassPopup from "../../components/SwitchClassPopup/SwitchClassPopup"
import EditStudentProfile from "./EditStudentProfile"
import MobileHeader from "../../components/MobileHeader/MobileHeader"
import { useDispatch } from "react-redux"
import { setHideScrollToTopButton } from "../../redux/slices/authSlice"
import { FETCH_POLICY } from "../../utils/constants"
import InfiniteScroll from "react-infinite-scroll-component"
interface AdminStudentPropsType {
    setDisplayStudents: (val: boolean) => void;
    displayStudents: boolean
    selectedClass: ClassroomObjectType | undefined
    isFullView: boolean;
    setisFullView: (value: boolean) => void;
    setisSearchClick: (value: boolean) => void;
    isSearchClick: boolean;
}

export interface Students {
    sId: string;
    sAvatarUrl: string;
    userByUId: {
        uFullName: string;
        uEmailId: string;
        uPhoneNumber: string;
        uUserName: string;
        uUserPassword: string;
    }
}

interface StudentsType {
    nodes: Students[],
    totalCount: number
}

const AdminStudents = ({ setDisplayStudents, displayStudents, selectedClass, isFullView, setisFullView, setisSearchClick, isSearchClick }: AdminStudentPropsType) => {
    const { backArrow, User, studentCheckboxChecked, studentCheckboxOutline, userImage, searchCloseIcon } = images;
    const [searchValue, setSearchValue] = useState("");
    const [displayStudentForm, setDisplayStudentForm] = useState<boolean>(false);
    const [studentsList, setStudentsList] = useState<StudentsType>();
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const limit = 50;
    const [checkedStudents, setCheckedStudents] = useState<string[]>([]);
    const { error } = ToasterWithMessage();
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
    const [openSwitchClassPopup, setOpenSwitchClassPopup] = useState(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [studentData, setStudentData] = useState<Students>();
    const baseUrl = process.env.REACT_APP_FRONTEND_URL;
    const studentNameRef = useRef<(HTMLDivElement | null)[]>([]);
    const [visibleTooltips, setVisibleTooltips] = useState<boolean[]>([]);
    const { isMobileScreen } = getScreenDimensions();
    const dispatch = useDispatch();

    const [getStudentsByClass] = useLazyQuery(GET_STUDENTS_BY_CLASSROOM, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: (data) => {
            setStudentsList(prev => {
                if (!prev || offset === 0) {
                    return data?.allStudents;
                }
                // Merge new nodes with previous nodes for infinite scroll
                return {
                    ...data.allStudents,
                    nodes: [
                        ...(prev?.nodes || []),
                        ...(data.allStudents?.nodes || [])
                    ]
                };
            });
            if (data?.allStudents?.nodes) {
                setHasMore(data.allStudents.nodes.length === limit && (offset + limit) < (data.allStudents.totalCount || 0));
            }
            setIsSelectAllChecked(checkedStudents?.length > 0 ? data?.allStudents?.length === checkedStudents?.length : false);
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    useEffect(() => {
        if (selectedClass) {
            setOffset(0);
            setHasMore(true);
            getStudentsByClass({
                variables: {
                    cId: selectedClass?.cId,
                    searchInput: `%${searchValue.trim()}%`,
                    limit,
                    offset: 0
                }
            })
        }
    },[selectedClass, refetch, displayStudentForm])

    useEffect(() => {
        if (!openSwitchClassPopup) {
            setCheckedStudents([]);
            setIsSelectAllChecked(false);
        }
    }, [openSwitchClassPopup])

    //To search students
    const handleSearchDebounce = useCallback(
        debounce((value) => {
            setSearchValue(value);
            setOffset(0);
            if(value.trim() === ""){
                setStudentsList(undefined);
            }
            if(value?.trim().length >= 3 || value.trim() === "") {
                getStudentsByClass({
                    variables: {
                        cId: selectedClass?.cId,
                        searchInput: `%${value.trim()}%`,
                        limit,
                        offset: 0
                    }
                });
            }
        }, 500),
        [getStudentsByClass]
    );
    // Infinite scroll fetch more
    const fetchMoreStudents = () => {
        const newOffset = offset + limit;
        setOffset(newOffset);
        getStudentsByClass({
            variables: {
                cId: selectedClass?.cId,
                searchInput: `%${searchValue.trim()}%`,
                limit,
                offset: newOffset
            }
        });
    };

    const handleCheckboxChange = (studentId: string) => {
        setOpenSwitchClassPopup(false);
        setCheckedStudents((prevState) => {
          const newCheckedStudents = prevState.includes(studentId)
            ? prevState.filter((id) => id !== studentId)
            : [...prevState, studentId];
          setIsSelectAllChecked(newCheckedStudents.length === studentsList?.nodes.length);
          return newCheckedStudents;
        });
      };  

      const handleCheckAll = () => {
        setOpenSwitchClassPopup(false);
        if (isSelectAllChecked) {
          setCheckedStudents([]);
        } else {
          setCheckedStudents(studentsList?.nodes.map((student) => student.sId) || []);
        }
        setIsSelectAllChecked(!isSelectAllChecked);
      };

    const handleClick = (data: Students) => {
        setDisplayStudentForm(true);
        setStudentData(data);
        setCheckedStudents([]);
        setIsSelectAllChecked(false);
    }

    const handleMouseEnter = (index: number) => {
        const ref = studentNameRef.current[index];
      
        if (ref) {
          const isOverflowing = ref.scrollWidth > ref.clientWidth;
          setVisibleTooltips((prev) => {
            const updated = [...prev];
            updated[index] = isOverflowing;
            return updated;
          });
        }
    };

    const handleMouseLeave = (index: number) => {
    setVisibleTooltips((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
    });
    };

    const handleBackClick = () => {
        setDisplayStudents(false);
    }

    const handleCloseSearch = () => {
        setisSearchClick(false);
        setSearchValue("");
        handleSearchDebounce("");
    };

    const searchOnChange = (event: { target: { value: string; }; }) => {
        setSearchValue(event.target.value);
        if (handleSearchDebounce){
          handleSearchDebounce(event?.target?.value);
        }
    };

    const handleSearchClick = () => {
        setisSearchClick(true);
    }

    useEffect(() => {
        dispatch(setHideScrollToTopButton({hideScrollToTopButton: !displayStudentForm}));
    }, [displayStudentForm])

    return (
        <>
            {(!displayStudentForm) ? (
                <div className="AdminStudent">
                    {
                        (isMobileScreen && isSearchClick) ?
                        <div className={'adminStudentSearch'}>
                                <input id="searchBox" type='text' placeholder='Search' onChange={searchOnChange} value={searchValue} className='SearchInputFieldStyle' />
                                {(isMobileScreen && isSearchClick) && <img src={searchCloseIcon} alt='closeIcon' className='closeIcon' onClick={handleCloseSearch} />}
                        </div>
                        :
                        <>
                            <div className="studentMobileHeaderContainer">
                                <div className="studentMobileHeaderRightContainer">
                                    <img alt="backArrow" src={backArrow} className="backArrow" onClick={handleBackClick}/>
                                    <div className="mobileHeaderTitleTextStyle">Students</div>
                                </div>
                                <MobileHeader handleSearchClick={handleSearchClick}/>
                            </div>
                            <TabsContentHeader
                                title={selectedClass?.cOriginalName ? selectedClass?.cOriginalName : '-'}
                                subtitle={formatClassroomStudentsCount(studentsList?.totalCount || 0)}
                                addLabel={""}
                                setDisplayForm={setDisplayStudents}
                                displayForm={displayStudents}
                                subImage={isMobileScreen ? userImage : backArrow}
                                handleSearchDebounce={handleSearchDebounce}
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                tab={"student"}
                                handleCheckAll={handleCheckAll}
                                isSelectAllChecked={isSelectAllChecked}
                                setOpenSwitchClassPopup={setOpenSwitchClassPopup}
                                checkedStudents={checkedStudents}
                            />
                        </>
                    }
                    <div className="studentListOuterContainer">
                        { (!studentsList ) ? <Loader /> : (
                            <InfiniteScroll
                                dataLength={studentsList?.nodes?.length || 0}
                                next={fetchMoreStudents}
                                hasMore={hasMore}
                                loader={
                                    <div className="scroll-loader">
                                        <CircularProgress size={30} />
                                    </div>
                                }
                                height={`100%`}
                                className={`${studentsList && studentsList?.totalCount > 0 ? 'studentListContainer' : 'forNoDataFound'}`}
                            >
                                {
                                    studentsList?.totalCount ?
                                        studentsList?.nodes?.map((student: Students, index: number) => {
                                            return (
                                                <div className="studentCardMainContainer" key={`${index}-${student?.sId}`}> 
                                                    <div className="studentCheckBox">
                                                        <Checkbox
                                                            icon={<img src={studentCheckboxOutline} alt="Unchecked" height={14} width={14} />}
                                                            checkedIcon={<img src={studentCheckboxChecked} alt="Checked" height={13} width={13} />}
                                                            checked={checkedStudents.includes(student.sId)}
                                                            onChange={() => handleCheckboxChange(student.sId)}
                                                            style={{
                                                                backgroundColor: checkedStudents.includes(student.sId) ? "var(--color-steelblue)" : "transparent"
                                                            }}
                                                        />
                                                    </div>
                                                    <div className='studentCardContainer' onClick={() => handleClick(student)}>
                                                        <div className="studentImageCtn">
                                                            <img 
                                                                src={ student?.sAvatarUrl ? baseUrl + student?.sAvatarUrl : User} 
                                                                alt="studentImage" 
                                                                className={student?.sAvatarUrl ? "studentImg" : "studentImgDefault"} 
                                                            />
                                                        </div>
                                                        <Tooltip
                                                            title={student?.userByUId?.uFullName}
                                                            open={visibleTooltips[index] || false}
                                                            placement="bottom"
                                                            disableInteractive
                                                        >
                                                            <div 
                                                                className="studentName" 
                                                                ref={(el) => ( studentNameRef.current[index] = el)} 
                                                                onMouseEnter={() => handleMouseEnter(index)} 
                                                                onMouseLeave={() => handleMouseLeave(index)}
                                                            >
                                                            {student?.userByUId?.uFullName}
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            )
                                        })
                                        :
                                        <div className='noDataFoundStyle'>No data found</div>
                                }
                            </InfiniteScroll>
                        )}
                    </div>
                    <SwitchClassPopup 
                        open={checkedStudents.length > 0 && openSwitchClassPopup} 
                        setOpen={setOpenSwitchClassPopup}
                        selectedClass={selectedClass} 
                        checkedStudents={checkedStudents}
                        setRefetch={setRefetch}
                        refetch={refetch}
                    />
                </div>
            ) : (
                <EditStudentProfile 
                    displayStudentForm={displayStudentForm} 
                    setDisplayStudentForm={setDisplayStudentForm} 
                    studentData={studentData}                   
                />
            )}
        </>
    )
}
export default AdminStudents;
